import React from 'react';
import { Box, Button, IconButton, Typography, alpha, useTheme } from '@mui/material';
import { CloseIcon, LockOutlinedIcon, ErrorOutlineIcon } from '@/components/icons';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

export type WidgetNoticeVariant =
  | 'mic_blocked'
  | 'transcribe_failed'
  | 'playback_failed';

interface WidgetNoticeProps {
  variant: WidgetNoticeVariant;
  /** Optional override for the body copy (e.g. tweak copy for non-network failures). */
  body?: string;
  onDismiss: () => void;
  /** Optional retry handler. When provided, a `Retry` button is rendered. */
  onRetry?: () => void;
}

const COPY: Record<WidgetNoticeVariant, { title: string; body: string }> = {
  mic_blocked: {
    title: 'Microphone blocked',
    body: 'Allow access in your browser settings.',
  },
  transcribe_failed: {
    title: "Couldn't transcribe",
    body: 'Network error. Tap retry or type instead.',
  },
  playback_failed: {
    title: "Couldn't play recording",
    body: 'You can still send it.',
  },
};

/**
 * Full-width dismissible notice rendered above the chat input row.
 * Variants: orange/warning for soft failures the user can route around
 * (mic permission, playback decode), red/error for transcription
 * failures that block the send pipeline. Auto-dismissal (when the user
 * starts a new recording or sends a message) is the parent's
 * responsibility.
 */
export function WidgetNotice({ variant, body, onDismiss, onRetry }: WidgetNoticeProps) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const isWarning = variant === 'mic_blocked' || variant === 'playback_failed';
  const palette = isWarning ? theme.palette.warning : theme.palette.error;

  const copy = COPY[variant];
  const Icon = isWarning ? LockOutlinedIcon : ErrorOutlineIcon;
  const livePoliteness = variant === 'mic_blocked' ? 'assertive' : 'polite';

  return (
    <Box
      role="alert"
      aria-live={livePoliteness}
      data-testid={`widget-notice-${variant}`}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.5,
        py: 1,
        mb: 1,
        borderRadius: 2,
        border: `1px solid ${palette.main}`,
        backgroundColor: alpha(palette.light, 0.2),
      }}
    >
      <Icon sx={{ fontSize: 18, color: palette.main, mt: '2px', flexShrink: 0 }} aria-hidden />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: palette.dark }}>
          {copy.title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {body ?? copy.body}
        </Typography>
        {onRetry && (
          <Button
            size="small"
            onClick={onRetry}
            sx={{
              mt: 0.5,
              textTransform: 'none',
              color: palette.dark,
              fontWeight: 600,
              p: 0,
              minWidth: 0,
              '&:hover': { background: 'transparent', textDecoration: 'underline' },
            }}
          >
            Retry
          </Button>
        )}
      </Box>
      <IconButton
        size="small"
        lang={locale}
        aria-label={t('feedback.dismissNotice')}
        onClick={onDismiss}
        sx={{ p: 0.5, color: 'text.secondary' }}
      >
        <CloseIcon sx={{ fontSize: 16 }} aria-hidden />
      </IconButton>
    </Box>
  );
}
